import { useCallback } from 'react'
import useFlowStore from '../../../components/chatflow/store'
import { toNodePayload } from '../utils'
import { API_PATHS } from '@/constants'
import apiClient from '@/services/apiClient'
import i18n from '@/i18n'

/**
 * useFlowSaver — Exclusively responsible for persisting the flow to the API.
 *
 * Receives `nodes` from React Flow as a parameter (lives in the parent component).
 * Everything else (flowId, flow meta, feedback) comes from the Zustand store.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useFlowSaver({ nodes }: { nodes: any[] }) {
  const flowId    = useFlowStore((s) => s.flowId)
  const flow      = useFlowStore((s) => s.flow)
  const setSaving = useFlowStore((s) => s.setSaving)
  const setDirty  = useFlowStore((s) => s.setDirty)
  const showToast = useFlowStore((s) => s.showToast)

  const handleSave = useCallback(async () => {
    setSaving(true)
    try {
      const realNodes = nodes.filter((n) => n.type !== 'trigger')

      const payload = {
        name:                     flow?.name,
        startNodeId:              flow?.startNodeId || null,
        inactivityMessage:        flow?.inactivityMessage,
        inactivityTimeoutMinutes: flow?.inactivityTimeoutMinutes,
        nodes: realNodes.map((n) => ({
          ...toNodePayload(n.data),
          position: { x: n.position.x, y: n.position.y },
        })),
      }

      await apiClient.put(API_PATHS.FLOWS.SYNC_BY_ID(flowId!), payload)

      setDirty(false)
      showToast(i18n.t('flow.saved_toast'))
    } catch (err) {
      showToast((err as Error).message || i18n.t('flow.save_error'), 'error')
    } finally {
      setSaving(false)
    }
  }, [flowId, flow, nodes, setSaving, setDirty, showToast])

  return { handleSave }
}
